import { Copy } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { aiProviders, type AiProvider } from "../../lib/schedule-document/ai-providers";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

export const copyOnlyId = "copy";

const chipClass = "h-9 justify-start gap-2 rounded border-neutral-300 px-2.5 text-xs font-medium hover:bg-neutral-50";
const activeChipClass = "border-neutral-900 bg-neutral-100";

type Props = {
  activeId: string | null;
  onOpen: (provider: AiProvider) => void;
  onCopyOnly: () => void;
};

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">{title}</p>
      <div className="grid grid-cols-2 gap-1.5">{children}</div>
    </div>
  );
}

export function AiProviderTiles({ activeId, onOpen, onCopyOnly }: Props) {
  const { t } = useTranslation();
  const prefilledProviders = aiProviders.filter(provider => provider.promptParam !== null);
  const pasteProviders = aiProviders.filter(provider => provider.promptParam === null);

  const renderProvider = (provider: AiProvider) => (
    <Button key={provider.id} type="button" variant="outline" onClick={() => onOpen(provider)}
      className={cn(chipClass, activeId === provider.id && activeChipClass)}>
      <img src={provider.logo} alt="" className="size-5 shrink-0 object-contain" />
      {provider.name}
    </Button>
  );

  return (
    <div className="space-y-3">
      <Group title={t("ai.groupPrefilled")}>{prefilledProviders.map(renderProvider)}</Group>
      <Group title={t("ai.groupPaste")}>
        {pasteProviders.map(renderProvider)}
        <Button type="button" variant="outline" onClick={onCopyOnly}
          className={cn(chipClass, "border-dashed", activeId === copyOnlyId && activeChipClass)}>
          <Copy className="size-5 shrink-0 stroke-[1.5] text-neutral-600" />
          {t("ai.copyPrompt")}
        </Button>
      </Group>
    </div>
  );
}
